import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import { Theme, Search, ColorBadge, SelectOption, Select } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import moment from 'moment';
import accounting from 'accounting';
import routePaths from '../../route-paths';
import { AdminNumocoOrders } from './numoco-queries';
import { VColumn, VTable } from '../../components/v-table';
import { TitleService } from '../../services';
import { Breadcrumbs } from '../../store';
import useStickyState from '../../hooks/use-sticky-state';

const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    border: `1px solid ${Theme.tableBorderColor}`,
  },
  header: {
    padding: 10,
    display: 'flex',
    gap: 20,
    borderBottom: `1px solid ${Theme.grey10}`,
    alignItems: 'center',
  },
});

const NUMOCO_ORDER_STATUS = {
  OPEN: 'Open',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  PAID: 'Paid',
  COMPLETE: 'Complete',
  CANCELLED: 'Cancelled',
};

// const statusThemes = {
//   OPEN: { background: Theme.yellow30, color: Theme.text },
//   COMPLETE: { background: Theme.green30, color: Theme.text },
//   CANCELLED: { background: Theme.red30, color: Theme.text },
//   SHIPPED: { background: Theme.mauve30, color: Theme.text },
//   DELIVERED: { background: Theme.purple30, color: Theme.text },
//   PAID: { background: Theme.blue30, color: Theme.text },
// };

const statusThemes = {
  OPEN: 'yellow',
  COMPLETE: 'green',
  CANCELLED: 'red',
  SHIPPED: 'orange',
  DELIVERED: 'grey',
  PAID: 'teal',
};

const getThemeByStatus = (status) => {
  if (statusThemes[status]) return statusThemes[status];
  return 'grey';
};

const initialStatusState = [
  { value: 'OPEN', text: NUMOCO_ORDER_STATUS.OPEN },
  { value: 'SHIPPED', text: NUMOCO_ORDER_STATUS.SHIPPED },
  { value: 'DELIVERED', text: NUMOCO_ORDER_STATUS.DELIVERED },
];

const NumocoOrders = ({ history }) => {
  const classes = useStyles();
  const [selectedStatuses, setSelectedStatuses] = useStickyState(initialStatusState, 'numoco-orders-statuses');

  const queryArgs = useMemo(
    () => ({
      statuses: selectedStatuses.map((s) => s.value),
    }),
    [selectedStatuses],
  );

  const { data, loading } = useQuery(AdminNumocoOrders, {
    variables: queryArgs,
  });
  const [searchText, setSearchText] = useState('');

  const ordersList = data?.adminNumocoOrders || [];

  useEffect(() => {
    TitleService.setTitles('NUMOCO Orders');
    Breadcrumbs.set([
      {
        url: routePaths.numocoOrderManager,
        title: 'NUMOCO Orders',
      },
    ]);
  }, []);

  const columns = useMemo(() => {
    return [
      new VColumn({
        field: '_id',
        displayName: 'ID',
        sortable: true,
      }),
      new VColumn({
        field: 'order_number',
        displayName: 'Order #',
        sortable: true,
      }),
      new VColumn({
        field: 'created_at',
        displayName: 'Created',
        sortable: true,
        defaultSort: true,
        defaultSortDirection: 'desc',
        formatter: (value) => moment(value).format('MMM D, YYYY'),
      }),
      new VColumn({
        field: 'name',
        displayName: 'Name',
        sortable: true,
      }),
      new VColumn({
        field: 'supplier_company_name',
        displayName: 'Seller',
        sortable: true,
      }),
      new VColumn({
        field: 'buyer_company_name',
        displayName: 'Buyer',
        sortable: true,
      }),
      new VColumn({
        field: 'total_amount',
        displayName: 'Total',
        sortable: true,
        formatter: (value) => accounting.formatMoney(value),
      }),
      new VColumn({
        field: 'status',
        displayName: 'Status',
        sortable: true,
        formatter: (value) => <ColorBadge theme={getThemeByStatus(value)}>{value}</ColorBadge>,
      }),
    ];
  }, []);

  const statusOptions = useMemo(() => {
    return Object.entries(NUMOCO_ORDER_STATUS).map(([key, label]) => {
      return (
        <SelectOption checkbox key={key} value={key}>
          {label}
        </SelectOption>
      );
    });
  }, []);

  const handleSearch = (text) => {
    setSearchText(text);
  };

  const handleRowClick = (row) => {
    history.push(`${routePaths.numocoOrderManager}/order/${row._id}`);
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.header}>
        <Search onChange={handleSearch} className={classes.search} />
        <Select
          selectAll
          autoWidth
          multiple
          label="Order Status"
          selectedItems={selectedStatuses}
          onChange={(selected) => setSelectedStatuses([...selected])}
        >
          {statusOptions}
        </Select>
      </div>
      <VTable cols={columns} data={ordersList} loading={loading} searchText={searchText} onRowClick={handleRowClick} />
    </div>
  );
};

NumocoOrders.propTypes = {
  history: PropTypes.object,
};

export default NumocoOrders;
